import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import ProjectCard from '../components/projects/ProjectCard';
import ProjectFilter from '../components/projects/ProjectFilter';
import { Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '@/i18n/LanguageProvider';

const defaultProjects = [
  {
    id: 'demo-1',
    title: 'Casa Moderna',
    category: 'residential',
    cover_image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80',
    location: 'Milan',
    year: '2024'
  },
  {
    id: 'demo-2',
    title: 'Villa Serena',
    category: 'residential',
    cover_image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
    location: 'Rome',
    year: '2023'
  },
  {
    id: 'demo-3',
    title: 'Corporate Hub',
    category: 'commercial',
    cover_image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    location: 'Florence',
    year: '2024'
  },
  {
    id: 'demo-4',
    title: 'Boutique Hotel',
    category: 'hospitality',
    cover_image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80',
    location: 'Venice',
    year: '2023'
  },
  {
    id: 'demo-5',
    title: 'Casa Luce',
    category: 'residential',
    cover_image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80',
    location: 'Naples',
    year: '2024'
  },
  {
    id: 'demo-6',
    title: 'Design Studio',
    category: 'commercial',
    cover_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    location: 'Turin',
    year: '2023'
  }
];

export default function Projects() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const initialCategory = urlParams.get('category') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialCategory);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const category = params.get('category') || 'all';
    setActiveCategory(category);
  }, [window.location.search]);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => base44.entities.Project.list(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const displayProjects = projects.length > 0 ? projects : defaultProjects;

  const filteredProjects = activeCategory === 'all'
    ? displayProjects
    : displayProjects.filter(p => p.category === activeCategory);

  const categoryDescriptions = {
    all: 'Il nostro portfolio presenta una selezione di progetti di ristrutturazione e interior design realizzati nella provincia di Palermo.',
    Residenziale: 'L\'architettura residenziale è l\'incontro tra estetica, funzionalità e identità. Ogni casa è un luogo unico, pensato per accogliere le abitudini, i desideri e lo stile di chi la abita.',
    Commerciale: 'Progetti di ristrutturazione completa con il metodo B&P: dal sopralluogo iniziale fino alla consegna chiavi in mano, seguiamo ogni fase con attenzione ai dettagli.',
    'In Progress': 'Scopri i progetti in corso e la loro evoluzione, dalle prime idee fino alla realizzazione.',
    Outdoor: 'Progetti di spazi esterni: giardini, terrazzi e ambienti all\'aperto pensati in continuità con l\'architettura.'
  };

  return (
    <>
      <SEO 
        title={t('Progetti')}
        description={t('Scopri il portfolio di Bellomonte & Pensabene: progetti residenziali e commerciali di architettura e interior design a Palermo e provincia.')}
        keywords="portfolio architettura, progetti residenziali Palermo, interior design Palermo, ristrutturazioni"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: t('Progetti'),
          url: 'https://studioarchitetturabellomontepensabene.it/Progetti',
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: filteredProjects.length,
            itemListElement: filteredProjects.map((project, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: project.title,
              url: `https://studioarchitetturabellomontepensabene.it/ProjectDetail?id=${project.id}`
            }))
          }
        }}
      />
      <div className="pb-14 pt-20 md:pb-32 md:pt-32">
        <div className="mx-auto max-w-[1800px] px-6 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-20"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-[#2d2d2d] mb-8">
            {activeCategory === 'all' ? t('Progetti') : t(activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1))}
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20">
            <p className="text-[17px] leading-[1.7] text-[#5f5f5f] md:text-lg">
              {t(categoryDescriptions[activeCategory] || categoryDescriptions.all)}
            </p>
            <div className="lg:flex lg:justify-end lg:items-start">
              <ProjectFilter
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </div>
          </div>
        </motion.div>

        {/* Projects Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#74675a]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-7 md:grid-cols-2 md:gap-12">
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} currentCategory={activeCategory} />
            ))}
          </div>
        )}

        {filteredProjects.length === 0 && !isLoading && (
          <p className="text-center text-[#5f5f5f] py-20">
            {t('Nessun progetto trovato in questa categoria.')}
          </p>
        )}
      </div>
    </div>
    </>
  );
}